(ns io.daskul.webapp.widgets.form.lookup-create
  "Inline create form for lookup fields.

   Fetches create schema for a target resource, renders form fields,
   and submits via raw ajax POST (no re-frame navigation side effects).
   Used when a lookup dropdown has '+ Add New...' and the user clicks it."
  (:require
   [uix.core :as uix :refer [defui $]]
   [uix.re-frame :as urf]
   [ajax.core :as ajax]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.widgets.form.advanced-fields :as adv-fields]
   [io.daskul.webapp.widgets.form.advanced-section :as advanced-section]
   [io.daskul.webapp.widgets.form.errors :as form.errors]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.config.subs :as config.subs]))


(defui lookup-inline-create
  "Inline form for creating a new entity from a lookup field.

   Props:
   - resource: string - REST resource name (e.g., \"guardians\")
   - label: string - display name (e.g., \"Guardian\")
   - on-created: (fn [new-id]) - called on successful create
   - on-cancel: (fn []) - called when user cancels
   - form-field-component: component - renders individual fields
   - initial-data: map - optional pre-filled form values (e.g., {:customer-id \"uuid\"})
   - hidden-fields: set - optional field keywords to skip rendering (e.g., #{:customer-id})
   - advanced-fields: set - optional field keywords to render inside a collapsed
     \"More details\" disclosure instead of inline (progressive capture, SMS-106).
     Hidden values still submit; only their rendering is deferred."
  [{:keys [resource label on-created on-cancel form-field-component
           initial-data hidden-fields advanced-fields]}]
  (let [aggregates (urf/use-subscribe [::config.subs/aggregates])
        [schema set-schema!] (uix/use-state nil)
        [schema-loading? set-schema-loading!] (uix/use-state true)
        [schema-error set-schema-error!] (uix/use-state nil)
        [form-data set-form-data!] (uix/use-state (or initial-data {}))
        [saving? set-saving!] (uix/use-state false)
        ;; Structured: {:message "..." :field-errors {:name "..."}} or nil
        [form-error set-form-error!] (uix/use-state nil)
        agg-key (keyword resource)]

    ;; Fetch create schema on mount
    (uix/use-effect
     (fn []
       (set-schema-loading! true)
       (ajax/GET (str (agg.events/get-api-base) "/_schema/" resource "/create")
                 {:headers (agg.events/get-auth-header)
                  :response-format (ajax/json-response-format {:keywords? true})
                  :handler (fn [response]
                             (set-schema! (agg.events/normalize-schema response aggregates))
                             (set-schema-loading! false))
                  :error-handler (fn [_err]
                                   (set-schema-error! "Failed to load form")
                                   (set-schema-loading! false))})
       js/undefined)
     [resource aggregates])

    ;; Apply schema defaults to form data (e.g., branch-id from JWT context)
    (uix/use-effect
     (fn []
       (when schema
         (let [defaults (->> (:fields schema)
                             (keep (fn [field]
                                     (when (some? (:default field))
                                       [(:name field) (:default field)])))
                             (into {}))]
           (when (seq defaults)
             (set-form-data! #(merge defaults %)))))
       js/undefined)
     [schema])

    ($ ui/box {:p "md"}
      (cond
        schema-loading?
        ($ ui/centered-loader {:label "Loading form..."})

        schema-error
        ($ ui/stack {:gap "sm"}
          ($ ui/error-alert schema-error)
          ($ ui/button {:type "button" :variant "subtle" :color "gray" :size "sm" :onClick on-cancel}
            "Cancel"))

        :else
        (let [fields (cond->> (:fields schema)
                      (seq hidden-fields)
                      (remove #(contains? hidden-fields (:name %))))
              {:keys [primary advanced]} (adv-fields/split-fields fields advanced-fields)
              render-field form-field-component
              ;; Render one field — shared by the primary (inline) loop and the
              ;; collapsed "More details" disclosure so both paths stay identical.
              render-one
              (fn [field]
                (let [field-key (:name field)
                      raw-err (get-in form-error [:field-errors field-key])]
                  ($ render-field
                    {:key (name field-key)
                     :agg-key agg-key
                     :field field
                     :value (get form-data field-key)
                     :on-change #(set-form-data! (assoc form-data field-key %))
                     ;; A widget that writes a sibling column (value + its unit)
                     ;; needs more than its own key. Without this the unit picker
                     ;; renders and silently changes nothing.
                     :set-form-field! #(set-form-data! (assoc form-data %1 %2))
                     :form-data form-data
                     :external-error (form.errors/humanize-error raw-err)
                     :on-clear-error #(set-form-error!
                                        (update form-error :field-errors dissoc field-key))})))
              handle-submit
              (fn []
                (set-saving! true)
                (set-form-error! nil)
                (ajax/POST (str (agg.events/get-api-base) "/" resource)
                           {:params form-data
                            :headers (agg.events/get-auth-header)
                            :format (ajax/json-request-format)
                            :response-format (ajax/json-response-format {:keywords? true})
                            :handler (fn [response]
                                       (set-saving! false)
                                       (on-created (:id response)))
                            :error-handler (fn [error]
                                             (set-saving! false)
                                             (set-form-error! (form.errors/parse-error-response error)))}))
              error-msg (form.errors/form-level-error-message form-error fields)]
          ($ :form {:onSubmit (fn [e]
                                (.preventDefault e)
                                (.stopPropagation e)
                                (handle-submit))}
            ($ ui/stack {:gap "sm"}
              (when error-msg
                ($ ui/error-alert {:mb "xs"} error-msg))

              ;; Primary fields inline
              (for [field primary]
                (render-one field))

              ;; Advanced fields collapsed under "More details" (nothing rendered
              ;; when there are none — see advanced-section)
              ($ advanced-section/advanced-section
                {:fields advanced
                 :render-field render-one})

              ($ ui/group {:justify "flex-end" :gap "sm"}
                ($ ui/button {:type "button" :variant "subtle" :color "gray" :size "sm"
                              :onClick (fn []
                                         (set-form-error! nil)
                                         (on-cancel))
                              :disabled saving?}
                  "Cancel")
                ($ ui/button {:type "submit" :size "sm" :loading saving?}
                  "Create")))))))))
